import React, { useState, useEffect, useRef } from "react";
import { Box } from "./wordCounterStyle";
import { ToolsHeader } from "../components/ToolsHeader";
import { isInIframe } from "../../utils/is-in-iframe";

const STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "by",
  "for",
  "from",
  "has",
  "he",
  "in",
  "is",
  "it",
  "its",
  "of",
  "on",
  "that",
  "the",
  "to",
  "was",
  "were",
  "will",
  "with",
  "this",
  "but",
  "they",
  "have",
  "had",
  "what",
  "when",
  "where",
  "who",
  "which",
  "why",
  "how",
  "all",
  "any",
  "both",
  "each",
  "few",
  "more",
  "most",
  "other",
  "some",
  "such",
  "no",
  "nor",
  "not",
  "only",
  "own",
  "same",
  "so",
  "than",
  "too",
  "very",
  "can",
  "just",
  "should",
  "now",
  "i",
  "you",
  "your",
  "we",
  "my",
  "me",
  "her",
  "his",
  "their",
  "our",
  "us",
  "am",
  "been",
  "being",
  "do",
  "does",
  "did",
  "doing",
  "would",
  "could",
  "might",
  "must",
  "shall",
  "into",
  "if",
  "then",
  "else",
  "again",
  "once",
  "there",
  "about",
  "against",
  "between",
  "through",
  "during",
  "before",
  "after",
  "above",
  "below",
  "up",
  "down",
  "out",
  "off",
  "over",
  "under",
]);

interface TopWord {
  word: string;
  count: number;
  density: string;
}

interface Stats {
  wordCount: number;
  charCount: number;
  charNoSpaces: number;
  sentenceCount: number;
  paragraphCount: number;
  avgWordLength: number;
  readingTime: number;
  topWords: TopWord[];
  uniqueKeywords: number;
}

interface SelectedStats {
  selectedWordCount: number;
  selectedCharCount: number;
}

const WordCounterTool: React.FC = () => {
  const [text, setText] = useState("");
  const [stats, setStats] = useState<Stats | null>(null);
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [selectedStats, setSelectedStats] = useState<SelectedStats>({
    selectedWordCount: 0,
    selectedCharCount: 0,
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    analyzeText(text);
  }, [text]);
  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [recommendations]);

  const isValidKeyword = (word: string): boolean => {
    word = word.toLowerCase();
    return (
      word.length > 2 &&
      !STOP_WORDS.has(word) &&
      !/^\d+$/.test(word) &&
      !/[!@#$%^&*(),.?":{}|<>]/.test(word)
    );
  };

  const analyzeText = (input: string) => {
    const words = input
      .trim()
      .split(/\s+/)
      .map((word) => word.toLowerCase().replace(/[.,!?;:'"]/g, ""))
      .filter((word) => word.length > 0);

    const wordCount = words.length;
    const charCount = input.length;
    const charNoSpaces = input.replace(/\s/g, "").length;
    const sentenceCount = input
      .split(/[.!?]+/)
      .filter((s) => s.trim().length > 0).length;
    const paragraphCount =
      input.split(/\n\n+/).filter((p) => p.trim().length > 0).length || 0;
    const avgWordLength = words.length
      ? Number((words.join("").length / words.length).toFixed(1))
      : 0;
    const readingTime = Math.ceil(wordCount / 200);

    const wordFrequency: Record<string, number> = {};
    words.forEach((word) => {
      if (isValidKeyword(word)) {
        wordFrequency[word] = (wordFrequency[word] || 0) + 1;
      }
    });

    const topWords = Object.entries(wordFrequency)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([word, count]) => ({
        word,
        count,
        density: wordCount ? ((count / wordCount) * 100).toFixed(1) : "0.0",
      }));

    const uniqueKeywords = Object.keys(wordFrequency).length;

    setStats({
      wordCount,
      charCount,
      charNoSpaces,
      sentenceCount,
      paragraphCount,
      avgWordLength,
      readingTime,
      topWords,
      uniqueKeywords,
    });

    const newRecommendations: string[] = [];
    if (wordCount > 300)
      newRecommendations.push(
        "Consider adding headings to break up your content"
      );
    if (wordCount / paragraphCount > 150)
      newRecommendations.push(
        "Try breaking long paragraphs into smaller ones for better readability"
      );
    if (wordCount < 50)
      newRecommendations.push(
        "This might be too short for a comprehensive article"
      );
    else if (wordCount > 1000)
      newRecommendations.push(
        "This length is ideal for a detailed blog post or article"
      );
    setRecommendations(newRecommendations);
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  };

  const analyzeSelectedText = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = text.substring(start, end);

      if (selectedText.trim().length > 0) {
        const words = selectedText
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0);
        const selectedWordCount = words.length;
        const selectedCharCount = selectedText.length;

        setSelectedStats({
          selectedWordCount,
          selectedCharCount,
        });
      } else {
        resetSelectedStats();
      }
    }
  };

  const resetSelectedStats = () => {
    setSelectedStats({
      selectedWordCount: 0,
      selectedCharCount: 0,
    });
  };

  const clearText = () => {
    setText("");
    resetSelectedStats();
  };

  return (
    <Box>
      <div className={`wordcounter-container ${isInIframe && "interDisplay"}`}>
        {!isInIframe && (
          <ToolsHeader
            title="Word Counter"
            description="Quickly analyze your text and receive accurate, actionable
                insights with clear reports."
          />
        )}

        <div className="wordcounter-input-section">
          <div className="wordcounter-top-bar">
            <span id="wordcounter-selected-word-count">
              {selectedStats.selectedWordCount} words
            </span>
            <span id="wordcounter-selected-char-count">
              {selectedStats.selectedCharCount} characters
            </span>
          </div>
          <textarea
            ref={textareaRef}
            value={text}
            id="wordcounter-text-input"
            onChange={(e) => {
              setText(e.target.value);
              resetSelectedStats();
            }}
            onMouseUp={analyzeSelectedText}
            onKeyUp={analyzeSelectedText}
            onBlur={resetSelectedStats}
            placeholder="Start typing or paste your text here..."
          />

          <div style={{ marginBottom: "0.5rem" }}>
            <span style={{ fontWeight: "600" }}>Note : </span>
            Select text to see word and character count.
          </div>
          <button
            id="wordcounter-clear-btn"
            className="wordcounter-clear-btn"
            onClick={clearText}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 6h18"></path>
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
            </svg>
            Clear Text
          </button>
        </div>

        {stats && (
          <div className="wordcounter-stats-grid">
            <div className="wordcounter-stat-card">
              <div className="wordcounter-stat-header">
                <svg
                  className="wordcounter-icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 7V4h16v3"></path>
                  <path d="M9 20h6"></path>
                  <path d="M12 4v16"></path>
                </svg>
                Basic Stats
              </div>
              <div className="wordcounter-stat-content">
                <p>Words: {stats.wordCount}</p>
                <p>Characters: {stats.charCount}</p>
                <p>Characters (no spaces): {stats.charNoSpaces}</p>
              </div>
            </div>

            <div className="wordcounter-stat-card">
              <div className="wordcounter-stat-header">
                <svg
                  className="wordcounter-icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="21" y1="10" x2="3" y2="10"></line>
                  <line x1="21" y1="6" x2="3" y2="6"></line>
                  <line x1="21" y1="14" x2="3" y2="14"></line>
                  <line x1="21" y1="18" x2="3" y2="18"></line>
                </svg>
                Structure
              </div>
              <div className="wordcounter-stat-content">
                <p>Sentences: {stats.sentenceCount}</p>
                <p>Paragraphs: {stats.paragraphCount}</p>
                <p>Avg. Word Length: {stats.avgWordLength}</p>
              </div>
            </div>

            <div className="wordcounter-stat-card">
              <div className="wordcounter-stat-header">
                <svg
                  className="wordcounter-icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#8b5cf6"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                Reading Time
              </div>
              <div className="wordcounter-stat-content">
                <p>
                  {stats.readingTime} minute{stats.readingTime !== 1 ? "s" : ""}{" "}
                  to read
                </p>
              </div>
            </div>
          </div>
        )}

        {stats && stats.topWords.length > 0 && (
          <div className="wordcounter-top-words">
            <div className="wordcounter-stat-header">
              <svg
                className="wordcounter-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#eab308"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
              </svg>
              Keyword Analysis
            </div>
            <div className="wordcounter-word-chips">
              {stats.topWords.map(({ word, count, density }) => (
                <div key={word} className="wordcounter-word-chip">
                  <div className="displayFlexBox">
                    <div className="worldColor">{word}</div>
                    <span className="wordcounter-density">{density}%</span>
                  </div>
                  <p className="wordcounter-count">
                    Used {count} time{count !== 1 ? "s" : ""}
                  </p>
                </div>
              ))}
            </div>
            <div className="wordcounter-keyword-stats">
              <p>Total Unique Keywords: {stats.uniqueKeywords}</p>
              <p>
                Keyword Density Range:{" "}
                {stats.topWords[stats.topWords.length - 1].density}% -{" "}
                {stats.topWords[0].density}%
              </p>
            </div>
          </div>
        )}

        {recommendations.length > 0 && (
          <div className="wordcounter-recommendations">
            <div className="wordcounter-stat-header">
              <svg
                className="wordcounter-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6366f1"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                <path d="m9 8 3 3-3 3"></path>
              </svg>
              Recommendations
            </div>
            <div>
              {recommendations.map((rec, index) => (
                <div key={index} className="wordcounter-recommendation-item">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <p>{rec}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Box>
  );
};

export default WordCounterTool;
